<template>
  <div class="container-fluid px-5 mt-4">

    <!-- Uvítací nadpis -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="dashboard-greeting-wrap">
          <span class="dashboard-greeting-emoji">{{ greetingData.emoji }}</span>
          <div>
            <h1 class="dashboard-welcome">{{ $t(greetingData.titleKey, { name: displayName }) }}</h1>
            <p class="dashboard-greeting">{{ $t(greetingData.subKey) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Stat boxy -->
    <div class="dashboard-stats-grid mb-4">
      <div class="dashboard-stat-box">
        <div class="dashboard-stat-box__header">
          <i class="bi bi-award dashboard-stat-box__icon"></i>
          <span class="dashboard-stat-box__label">{{ $t('dashboard.stat_certs') }}</span>
        </div>
        <span class="dashboard-stat-box__value">{{ statsLoading ? '—' : stats.certs_count }}</span>
      </div>
      <div class="dashboard-stat-box">
        <div class="dashboard-stat-box__header">
          <i class="bi bi-file-earmark dashboard-stat-box__icon"></i>
          <span class="dashboard-stat-box__label">{{ $t('dashboard.stat_files') }}</span>
        </div>
        <span class="dashboard-stat-box__value">{{ statsLoading ? '—' : stats.files_count }}</span>
      </div>
      <div class="dashboard-stat-box">
        <div class="dashboard-stat-box__header">
          <i class="bi bi-share dashboard-stat-box__icon"></i>
          <span class="dashboard-stat-box__label">{{ $t('dashboard.stat_shared_files') }}</span>
        </div>
        <span class="dashboard-stat-box__value">{{ statsLoading ? '—' : stats.shared_files_count }}</span>
      </div>
    </div>

    <!-- Akční boxy — vytvoření, ověření a šifrování -->
    <div class="dashboard-actions-wrap mb-4">
      <div class="d-flex gap-3">

        <!-- Přechod na stránku vytvoření certifikátu -->
        <div class="dashboard-action-box dashboard-action-box--clickable" @click="$router.push({ name: 'CertCreate' })">
          <div class="dashboard-action-box__icon">
            <img :src="iconPlus" alt="" class="icon-blue">
          </div>
          <div class="dashboard-action-box__content">
            <p class="dashboard-action-box__title">{{ $t('dashboard.action_create') }}</p>
            <p class="dashboard-action-box__sub">{{ $t('dashboard.action_create_sub') }}</p>
          </div>
        </div>

        <!-- Přechod na stránku ověření certifikátu -->
        <div class="dashboard-action-box dashboard-action-box--clickable" @click="$router.push({ path: '/verification' })">
          <div class="dashboard-action-box__icon">
            <img :src="iconMagnifying" alt="">
          </div>
          <div class="dashboard-action-box__content">
            <p class="dashboard-action-box__title">{{ $t('dashboard.action_search') }}</p>
            <p class="dashboard-action-box__sub">{{ $t('dashboard.action_search_sub') }}</p>
          </div>
        </div>

        <!-- Zašifrovat dokument -->
        <div class="dashboard-action-box dashboard-action-box--clickable" @click="showEncryptModal = true">
          <div class="dashboard-action-box__icon">
            <img :src="iconLock" alt="">
          </div>
          <div class="dashboard-action-box__content">
            <p class="dashboard-action-box__title">{{ $t('dashboard.action_encrypt') }}</p>
            <p class="dashboard-action-box__sub">{{ $t('dashboard.action_encrypt_sub') }}</p>
          </div>
        </div>

      </div>
      <div v-if="false" class="dashboard-actions-warning">
        <img :src="iconWarning" alt="" class="dashboard-actions-warning__icon">
        <span class="dashboard-actions-warning__badge">{{ notificationsCount }}</span>
      </div>
    </div>

    <!-- Systémová upozornění — neúplný profil -->
    <div class="row">
      <div v-if="showHowToCreateCertificate" class="col-12 mb-3">
        <div class="dashboard-notice dashboard-notice--info">
          <div class="dashboard-notice__text">
            <div class="">1. {{ $t('dashboard.stepsToCreate_1') }}</div>
            <router-link :to="{ name: 'ClientProfile' }" class="dashboard-notice__btn">
              {{ $t('dashboard.fillProfile') }}<i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
          <div class="dashboard-notice__text mt-2">2. {{ $t('dashboard.stepsToCreate_2') }}</div>
          <div class="dashboard-notice__text mt-2">3. {{ $t('dashboard.stepsToCreate_3') }}</div>
        </div>
      </div>
      <div v-if="!isLoading && !isAllDataForCert" class="col-12 mb-3">
        <div class="prof-card">
          <div class="prof-card__header">
            <span class="prof-card__title">
              <i class="bi bi-person-fill prof-card__icon"></i>
              {{ $t('dashboard.completeProfile') }}
            </span>
            <span class="prof-card__count">{{ profileDoneCount }} / {{ profileSteps.length }} {{ $t('dashboard.stepsDone') }}</span>
          </div>

          <div class="prof-card__steps">
            <template v-for="(step, i) in profileSteps" :key="i">
              <router-link :to="{ name: 'ClientProfile' }" class="prof-step" :class="profileStepClass(i)">
                <div class="prof-step__circle">
                  <i v-if="step.done" class="bi bi-check-lg"></i>
                  <span v-else>{{ i + 1 }}</span>
                </div>
                <span class="prof-step__label">{{ step.label }}</span>
                <span class="prof-step__sub">{{ step.sub }}</span>
              </router-link>
              <div v-if="i < profileSteps.length - 1" class="prof-step__line" :class="{ 'prof-step__line--done': step.done }"></div>
            </template>
          </div>

          <div class="prof-card__bar">
            <div class="prof-card__bar-fill" :style="{ width: profileProgressPercent + '%' }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Poslední aktivita + upload -->
    <div class="dashboard-bottom-grid">

      <!-- Poslední aktivita -->
      <div class="dashboard-card">
        <div class="dashboard-card__header">
          <span class="dashboard-card__title"><i class="bi bi-clock-history me-2"></i>{{ $t('dashboard.recent_activity') }}</span>
          <router-link :to="{ name: 'FilesPage' }" class="dashboard-card__link">
            {{ $t('dashboard.show_all') }} <i class="bi bi-arrow-right"></i>
          </router-link>
        </div>
        <div class="dashboard-activity-list">
          <div v-if="activityLoading" class="dashboard-activity-empty">—</div>
          <div v-else-if="activity.length === 0" class="dashboard-activity-empty">{{ $t('dashboard.no_activity') }}</div>
          <template v-else>
            <router-link
              v-for="item in activity"
              :key="item.uuid"
              class="dashboard-activity-item"
              :to="item.type === 'cert'
                ? { name: 'CertDetail', params: { uuid: item.uuid } }
                : { name: 'FileDetail', params: { uuid: item.uuid } }"
            >
              <div class="dashboard-activity-item__icon">
                <i :class="item.type === 'cert' ? 'bi bi-award' : 'bi bi-file-earmark'"></i>
              </div>
              <div class="dashboard-activity-item__info">
                <span class="dashboard-activity-item__name">{{ item.name }}</span>
                <span class="dashboard-activity-item__type">{{ item.type === 'cert' ? $t('dashboard.type_cert') : $t('dashboard.type_file') }}</span>
              </div>
              <span class="dashboard-activity-item__date">{{ formatDate(item.created_at) }}</span>
            </router-link>
          </template>
        </div>
      </div>

      <!-- Upload -->
      <div class="dashboard-card">
        <div class="dashboard-card__header">
          <span class="dashboard-card__title"><i class="bi bi-cloud-arrow-up me-2"></i>{{ $t('dashboard.upload_title') }}</span>
        </div>

        <div
          class="dashboard-dropzone"
          :class="{ 'dashboard-dropzone--over': isDragOver, 'dashboard-dropzone--uploading': uploadingFileNames.length }"
          @dragover.prevent="!uploadingFileNames.length && (isDragOver = true)"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="!uploadingFileNames.length && handleDrop($event)"
          @click="!uploadingFileNames.length && $refs.fileInput.click()"
        >
          <input ref="fileInput" type="file" multiple style="display:none" @change="handleFileSelect">
          <template v-if="!uploadingFileNames.length">
            <i class="bi bi-cloud-arrow-up dashboard-dropzone__icon"></i>
            <p class="dashboard-dropzone__text">{{ $t('dashboard.upload_hint') }}</p>
            <p class="dashboard-dropzone__sub">PDF, DOCX, JPG, PNG, HEIC…</p>
          </template>
          <template v-else>
            <span class="spinner-border spinner-border-sm text-primary mb-1" role="status"></span>
            <p v-for="name in uploadingFileNames" :key="name" class="dashboard-dropzone__filename mb-0">{{ name }}</p>
          </template>
        </div>

      </div>

    </div>

    <!-- Modal: Nová složka -->
    <div v-if="showNewFolderModal" class="dashboard-modal-backdrop" @click.self="showNewFolderModal = false">
      <div class="dashboard-modal">
        <h6 class="dashboard-modal__title">{{ $t('dashboard.new_folder') }}</h6>
        <input
          v-model="newFolderName"
          type="text"
          class="form-control mb-3"
          :placeholder="$t('dashboard.folder_name_placeholder')"
          @keydown.enter="createFolder"
        >
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-sm btn-outline-secondary" @click="showNewFolderModal = false">{{ $t('general.cancel') }}</button>
          <button class="btn btn-sm btn-primary" @click="createFolder" :disabled="!newFolderName.trim() || creatingFolder">
            <span v-if="creatingFolder" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ $t('general.create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Zašifrovat dokument -->
    <div v-if="showEncryptModal" class="create-cert-overlay" @click.self="!encryptingFile && closeEncryptModal()">
      <div class="create-cert-modal" style="max-width:480px;">
        <div class="create-cert-modal__header">
          <div class="create-cert-modal__header-left">
            <img :src="iconLock" alt="" class="create-cert-modal__modal-icon" style="filter:invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)">
            <p class="create-cert-modal__title">{{ $t('dashboard.action_encrypt') }}</p>
          </div>
          <button class="create-cert-modal__close" @click="!encryptingFile && closeEncryptModal()" :disabled="encryptingFile">×</button>
        </div>
        <div class="create-cert-modal__body">
          <div
            class="create-cert-dropzone"
            :class="{ 'create-cert-dropzone--active': encryptDragging, 'create-cert-dropzone--filled': encryptFile }"
            @dragover.prevent="encryptDragging = true"
            @dragleave.prevent="encryptDragging = false"
            @drop.prevent="onEncryptDrop"
            @click="$refs.encryptInput.click()"
          >
            <img :src="iconLock" alt="" class="create-cert-dropzone__icon" style="opacity:.45;">
            <span v-if="!encryptFile" class="create-cert-dropzone__text">{{ $t('cert.uploadFile') }}</span>
            <span v-else class="create-cert-dropzone__filename">{{ encryptFile.name }}</span>
            <input ref="encryptInput" type="file" style="display:none" @change="onEncryptFileChanged">
          </div>
          <p class="create-cert-hint mt-2">{{ $t('cert.maxFileSizeText') }}</p>
          <div class="d-flex justify-content-end gap-3 mt-4">
            <button class="btn create-cert-btn-cancel" @click="closeEncryptModal" :disabled="encryptingFile">
              {{ $t('general.cancel') }}
            </button>
            <button class="btn btn-primary create-cert-btn-submit" @click="uploadAndEncrypt" :disabled="encryptingFile || !encryptFile">
              <span v-if="encryptingFile" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ encryptingFile ? $t('cert.processing') : $t('dashboard.action_encrypt') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Seznam certifikátů uživatele -->
    <!--
    <div class="row">
      <div class="col-12 mb-3">
        <SharedCertsListComponent></SharedCertsListComponent>
        <CertsListComponent></CertsListComponent>
      </div>
    </div>
    -->

  </div>
</template>

<script>
import http from "../http";
import { checkFileSize } from '@/utils/upload.js';
import iconPlus from '/public/img/new-design/icons/plus.svg';
import iconMagnifying from '/public/img/new-design/icons/magnifying.svg';
import iconWarning from '/public/img/new-design/icons/warning.svg';
import iconLock from '/public/img/new-design/icons/lock.svg';
import { loadingState } from '@/loading.js';

const EMPTY_CLIENT = {
  name: '',
  email: '',
  address: { line_1: '', line_2: '', city: '', country_code: '', postal_code: '' },
  verification: { document_type: '', document_number: '', expiration_date: '' },
};

export default {
  data() {
    return {
      client: {
        address: { line_1: '', line_2: '', city: '', country_code: '', postal_code: '' },
        verification: { document_type: '', document_number: '', expiration_date: '' },
      },
      isLoading: false,
      showHowToCreateCertificate: false,
      notificationsCount: 3,
      stats: { certs_count: 0, files_count: 0, shared_files_count: 0 },
      statsLoading: true,
      activity: [],
      activityLoading: true,
      isDragOver: false,
      uploadingFileNames: [],
      creatingFolder: false,
      showNewFolderModal: false,
      newFolderName: '',
      showEncryptModal: false,
      encryptFile: null,
      encryptDragging: false,
      encryptingFile: false,
      iconPlus,
      iconMagnifying,
      iconWarning,
      iconLock,
    };
  },

  mounted() {
    this.getAuthClient();
    this.getDashboardStats();
    this.getRecentActivity();
    if (this.$route.query.showHowToCreateCertificate) {
      this.showHowToCreateCertificate = this.$route.query.showHowToCreateCertificate;
    }
  },

  computed: {
    displayName() {
      return this.client.salutation || this.client.name || '';
    },

    greetingData() {
      const h = new Date().getHours();
      if (h >= 5  && h < 9)  return { emoji: '☕', titleKey: 'dashboard.greeting_morning_title',   subKey: 'dashboard.greeting_morning_sub' };
      if (h >= 9  && h < 12) return { emoji: '☀️', titleKey: 'dashboard.greeting_forenoon_title',  subKey: 'dashboard.greeting_forenoon_sub' };
      if (h >= 12 && h < 13) return { emoji: '🥗', titleKey: 'dashboard.greeting_noon_title',      subKey: 'dashboard.greeting_noon_sub' };
      if (h >= 13 && h < 18) return { emoji: '⛅', titleKey: 'dashboard.greeting_afternoon_title', subKey: 'dashboard.greeting_afternoon_sub' };
      if (h >= 18 && h < 22) return { emoji: '🌙', titleKey: 'dashboard.greeting_evening_title',   subKey: 'dashboard.greeting_evening_sub' };
      return { emoji: '✨', titleKey: 'dashboard.greeting_night_title', subKey: 'dashboard.greeting_night_sub' };
    },

    profileSteps() {
      return [
        { label: this.$t('dashboard.stepBasic'), sub: this.$t('dashboard.stepBasicSub'), done: this.isName },
        { label: this.$t('dashboard.stepAddress'), sub: this.$t('dashboard.stepAddressSub'), done: this.isAddress },
        { label: this.$t('dashboard.stepVerification'), sub: this.$t('dashboard.stepVerificationSub'), done: this.isAllVerificationData },
      ];
    },

    profileDoneCount() {
      return this.profileSteps.filter(s => s.done).length;
    },

    profileProgressPercent() {
      return Math.round((this.profileDoneCount / this.profileSteps.length) * 100);
    },

    isName() {
      return this.client.name && this.client.name.length !== 0;
    },
    isAddress() {
      return this.client.address.line_1.length !== 0 && this.client.address.city.length !== 0 && this.client.address.postal_code.length !== 0;
    },
    isAllVerificationData() {
      return this.client.verification && this.client.verification.document_type?.length !== 0 && this.client.verification.document_number?.length !== 0 && this.client.verification.expiration_date?.length !== 0;
    },
    isAllDataForCert() {
      return this.isName && this.isAddress && this.isAllVerificationData;
    },
  },

  methods: {
    closeEncryptModal() {
      this.showEncryptModal = false;
      this.encryptFile = null;
      this.encryptDragging = false;
      if (this.$refs.encryptInput) this.$refs.encryptInput.value = '';
    },

    onEncryptDrop(e) {
      this.encryptDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) this.encryptFile = file;
    },

    onEncryptFileChanged(e) {
      const file = e.target.files[0];
      if (file && !checkFileSize(file)) {
        e.target.value = '';
        return;
      }
      if (file) this.encryptFile = file;
    },

    uploadAndEncrypt() {
      if (!this.encryptFile || this.encryptingFile) return;
      this.encryptingFile = true;
      const body = new FormData();
      body.append('file', this.encryptFile);
      http.post('/files/upload', body).then((response) => {
        const uuid = response.data?.file?.uuid;
        if (uuid) {
          this.closeEncryptModal();
          this.$router.push({ name: 'FileDetail', params: { uuid }, query: { openEncryption: '1' } });
        }
      }).finally(() => {
        this.encryptingFile = false;
      });
    },

    profileStepClass(i) {
      if (this.profileSteps[i].done) return 'prof-step--done';
      const allPrevDone = this.profileSteps.slice(0, i).every(s => s.done);
      return allPrevDone ? 'prof-step--current' : 'prof-step--upcoming';
    },

    getRecentActivity() {
      http.get('/recent-activity').then((response) => {
        this.activity = response.data.items;
        this.activityLoading = false;
      });
    },

    formatDate(dateStr) {
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - date) / 86400000);
      if (diffDays === 0) return this.$t('dashboard.date_today');
      if (diffDays === 1) return this.$t('dashboard.date_yesterday');
      return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
    },

    handleDrop(e) {
      this.isDragOver = false;
      this.uploadFiles(e.dataTransfer.files);
    },

    handleFileSelect(e) {
      this.uploadFiles(e.target.files);
      e.target.value = '';
    },

    uploadFiles(files) {
      // Příliš velké soubory vynecháme (checkFileSize ukáže hlášku), ostatní se nahrají
      const allowedFiles = Array.from(files).filter(f => checkFileSize(f));
      if (!allowedFiles.length) return;
      this.uploadingFileNames = allowedFiles.map(f => f.name);
      const uploads = allowedFiles.map(f => {
        const formData = new FormData();
        formData.append('file', f);
        return http.post('/files/upload', formData);
      });
      Promise.all(uploads).then(() => {
        this.getDashboardStats();
        this.getRecentActivity();
      }).finally(() => {
        this.uploadingFileNames = [];
      });
    },

    openNewFolderModal() {
      this.newFolderName = '';
      this.showNewFolderModal = true;
    },

    createFolder() {
      if (!this.newFolderName.trim() || this.creatingFolder) return;
      this.creatingFolder = true;
      http.post('/files/folders/create', { name: this.newFolderName.trim() }).then(() => {
        this.showNewFolderModal = false;
        this.$router.push({ name: 'FilesPage' });
      }).finally(() => {
        this.creatingFolder = false;
      });
    },

    getDashboardStats() {
      http.get('/dashboard-stats').then((response) => {
        this.stats = response.data;
        this.statsLoading = false;
      });
    },

    getAuthClient() {
      let self = this;
      self.isLoading = true;
      http.get('/get-auth-client').then((response) => {
        self.client = response.data.client;
        if (!response.data.client.address) self.client.address = { ...EMPTY_CLIENT.address };
        if (!response.data.client.verification) self.client.verification = { ...EMPTY_CLIENT.verification };
        self.isLoading = false;
        loadingState.active = false;
      });
    },
  },
};
</script>